import { Component, Input } from '@angular/core';
import { Filter } from '../../models/filter-models';
import { EquipmentType } from "../../models/equipment-type.enum";
import { TieringMode, Rarity } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { AutocompleteComponent } from '../../common/autocomplete/autocomplete.component';
import { FilterTitleComponent } from '../../common/filter-title/filter-title.component';
import { FilterService } from '../../services/filter.service';
import { ClusterJewelBase } from '../../models/item-data-models';
import { ItemDataService } from '../../services/item-data.service';
import { ClusterJewelTierService } from '../../services/tiering/cluster-jewel-tier.service';
import { BaseComponent } from '../../common/base.component';
import { ActiveRules, ActiveSpecialRules } from '../../services/filter-generator.service';
import { EquipmentTierPickerComponent } from '../../common/equipment-tier-picker/equipment-tier-picker.component';
import { FilterCheckboxGroupComponent } from '../../common/filter-checkbox-group/filter-checkbox-group.component';
import { SelectComponent, SelectOptionComponent } from '../../common/select/select.component';
import { SelectRarityComponent } from '../../common/select/select-rarity/select-rarity.component';
import { CustomClusterJewelTieringModeDialogComponent } from '../../custom-cluster-jewel-tiering-mode-dialog/custom-cluster-jewel-tiering-mode-dialog.component';
import { Economy } from '../../models/economy-models';

@Component({
  selector: 'app-influenced-jewellery-filters-poe1',
  imports: [FormsModule, FilterTitleComponent, AutocompleteComponent, EquipmentTierPickerComponent, FilterCheckboxGroupComponent,
    SelectComponent, SelectOptionComponent, SelectRarityComponent, CustomClusterJewelTieringModeDialogComponent],
  templateUrl: './influenced-jewellery-filters-poe1.component.html',
  styleUrl: './influenced-jewellery-filters-poe1.component.scss'
})
export class InfluencedJewelleryFiltersPoe1Component extends BaseComponent {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) activeRules!: ActiveRules;
  @Input({ required: true }) activeSpecialRules!: ActiveSpecialRules;

  jewelleryClasses = [
    { name: 'Amulets' },
    { name: 'Rings' },
    { name: 'Belts' },
  ];

  jewelTypes = this.poe2 ? [] : [
    { name: 'Crimson Jewel', image: '/img/gear/jewels/basicstr.webp' }, // TODO POE1 : correct image urls
    { name: 'Viridian Jewel', image: '/img/gear/jewels/basicdex.webp' },
    { name: 'Cobalt Jewel', image: '/img/gear/jewels/basicint.webp' },

    { name: 'Large Cluster Jewel', image: '/img/gear/jewels/newgembase3.webp' },
    { name: 'Medium Cluster Jewel', image: '/img/gear/jewels/newgembase2.webp' },
    { name: 'Small Cluster Jewel', image: '/img/gear/jewels/newgembase1.webp' },

    { name: 'Murderous Eye Jewel', image: '/img/gear/jewels/murderouseye.webp' },
    { name: 'Searching Eye Jewel', image: '/img/gear/jewels/searchingeye.webp' },
    { name: 'Hypnotic Eye Jewel', image: '/img/gear/jewels/rivetedeye.webp' },
    { name: 'Ghastly Eye Jewel', image: '/img/gear/jewels/ghastlyeye.webp' },
  ];

  influences: { name: string, image: string }[] = [];
  syndicateMembers!: { name: string, image?: string }[];
  classes: { name: string, image: string }[] = [];

  excellentClusterJewelBases: { iLevel: number, jewels: ClusterJewelBase[] }[] = [
    { iLevel: 84, jewels: [] },
    { iLevel: 75, jewels: [] },
    { iLevel: 68, jewels: [] },
    { iLevel: 50, jewels: [] },
    { iLevel: 1, jewels: [] },
  ];
  goodClusterJewelBases: { iLevel: number, jewels: ClusterJewelBase[] }[] = [
    { iLevel: 84, jewels: [] },
    { iLevel: 75, jewels: [] },
    { iLevel: 68, jewels: [] },
    { iLevel: 50, jewels: [] },
    { iLevel: 1, jewels: [] },
  ];

  clusterJewelBaseTypeMap!: Record<string, string>;

  Rarity = Rarity;
  EquipmentType = EquipmentType;
  Economy = Economy;

  clusterJewelMinItemLevelOptions = [1, 50, 68, 75, 84];
  showCustomTieringDialog = false;

  constructor(
    private filterService: FilterService,
    private itemDataService: ItemDataService,
    private clusterJewelTierService: ClusterJewelTierService,
  ) {
    super();
    this.classes = this.itemDataService.getClasses();
    this.influences = this.itemDataService.getInfluences();
    this.syndicateMembers = this.itemDataService.getAllSyndicateMembers().filter(m => m.name !== 'All');

    this.rebuildClusterJewelBases();

    this.clusterJewelBaseTypeMap = {
      'Large Cluster Jewel': '/poe1/img/gear/jewels/newgembase3.webp',
      'Medium Cluster Jewel': '/poe1/img/gear/jewels/newgembase2.webp',
      'Small Cluster Jewel': '/poe1/img/gear/jewels/newgembase1.webp',
    };
  }

  private rebuildClusterJewelBases() {
    const sortClusterJewelBases = (a: ClusterJewelBase, b: ClusterJewelBase) => { return (b.passiveNum ?? 0) - (a.passiveNum ?? 0); };
    const iLevels = [84, 75, 68, 50, 1];

    const excellent = this.clusterJewelTierService.getExcellentClusterJewelBases();
    const good = this.clusterJewelTierService.getGoodClusterJewelBases();

    for (let i = 0; i < iLevels.length; i++) {
      this.excellentClusterJewelBases[i].jewels = excellent.filter(j => j.minItemLevel === iLevels[i]).sort(sortClusterJewelBases);
      this.goodClusterJewelBases[i].jewels = good.filter(j => j.minItemLevel === iLevels[i]).sort(sortClusterJewelBases);
    }
  }

  toggleHighlightInfluencedJewellery = () => {
    this.filter.showInfluencedJewellery = !this.filter.showInfluencedJewellery;
    this.updateFilter();
  }

  toggleHighlightVeiledJewellery = () => {
    this.filter.showVeiledJewellery = !this.filter.showVeiledJewellery;
    this.updateFilter();
  }

  toggleHighlightAnointedAmulets = () => {
    this.filter.showAnointedAmulets = !this.filter.showAnointedAmulets;
    this.updateFilter();
  }

  toggleHighlightCorruptedImplicitJewels = () => {
    this.filter.showCorruptedImplicitJewels = !this.filter.showCorruptedImplicitJewels;
    this.updateFilter();
  }

  toggleHighlightCorruptedImplicitJewellery = () => {
    this.filter.showCorruptedImplicitJewellery = !this.filter.showCorruptedImplicitJewellery;
    this.updateFilter();
  }

  toggleHighlightExcellentClusterJewelBases() {
    this.filter.showExcellentClusterJewelBases = !this.filter.showExcellentClusterJewelBases;
    if (!this.filter.showExcellentClusterJewelBases) {
      this.filter.showGoodClusterJewelBases = false;
    }
    this.updateFilter();
  }


  getClusterJewelTierCutoffLabel(tier: 'Excellent' | 'Good'): string {
    const values = this.clusterJewelTierService.getSelectedTierCutoffValues();
    const tv = values.find(v => v.tier === tier);
    if (!tv) return '';
    const currencyShort = tv.baseCurrency === 'Chaos Orb' ? 'c' : tv.baseCurrency === 'Exalted Orb' ? ' ex' : ' div';
    return `${tv.value}${currencyShort}`;
  }

  clusterJewelTieringModeChanged(mode: TieringMode) {
    this.clusterJewelTierService.setTieringMode(mode, this.filter);
    this.rebuildClusterJewelBases();
    this.filterService.updateFilter(true);
  }

  openCustomTieringDialog() {
    this.showCustomTieringDialog = true;
  }

  closeCustomTieringDialog() {
    this.showCustomTieringDialog = false;
    this.rebuildClusterJewelBases();
    this.updateFilter();
  }

  updateFilter() {
    this.filterService.updateFilter();
  }
}
